import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import Button from '../../../components/atoms/forms/Button';

// Sticky bottom bar shown only on the Day Of phase. Flips a
// `route-ops:dispatched:<stockingDate>` flag in localStorage and toggles the
// button to "Dispatched ✓". Day rollover (a new active stocking date) gets a
// fresh flag automatically; stale flags > 2 days old are cleared on mount.

const STORAGE_PREFIX = 'route-ops:dispatched:';
const MAX_AGE_DAYS = 2;

function keyFor(stockingDate) {
  return `${STORAGE_PREFIX}${stockingDate}`;
}

function readFlag(stockingDate) {
  try {
    return window.localStorage.getItem(keyFor(stockingDate)) === 'true';
  } catch (e) {
    return false;
  }
}

function writeFlag(stockingDate) {
  try {
    window.localStorage.setItem(keyFor(stockingDate), 'true');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('dispatched flag write failed', e);
  }
}

// Exported for tests. Calendar-day based — see useDailyChecklistDraft for
// the rationale (avoids losing a draft created yesterday morning by midday).
export function cleanupStaleDispatchKeys(now = new Date()) {
  const midnight = new Date(now);
  midnight.setHours(0, 0, 0, 0);
  const cutoffMs = midnight.getTime() - MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
  const keysToRemove = [];
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i);
    if (!key || !key.startsWith(STORAGE_PREFIX)) continue; // eslint-disable-line no-continue
    const datePart = key.slice(STORAGE_PREFIX.length);
    const dateMs = Date.parse(datePart);
    if (Number.isNaN(dateMs) || dateMs < cutoffMs) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((k) => window.localStorage.removeItem(k));
}

let cleanedThisSession = false;

// Test-only reset.
export function _resetDispatchCleanupFlag() {
  cleanedThisSession = false;
}

const StyledDiv = styledComponent('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: theme.spacing.gapMd,
  minWidth: '220px',

  '& .db-status': {
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
    textAlign: 'right',
    maxWidth: '260px',
    lineHeight: 1.4,
  },
});

function DispatchBar({ stockingDate, enabled, disabledTooltip }) {
  const [dispatched, setDispatched] = useState(false);

  useEffect(() => {
    if (!cleanedThisSession) {
      cleanupStaleDispatchKeys();
      cleanedThisSession = true;
    }
    setDispatched(readFlag(stockingDate));
  }, [stockingDate]);

  const handleDispatch = useCallback(() => {
    if (!enabled || dispatched) return;
    writeFlag(stockingDate);
    setDispatched(true);
  }, [enabled, dispatched, stockingDate]);

  const status = (() => {
    if (dispatched) return 'Routes are dispatched for today.';
    if (!enabled) return 'Complete both Day Of sections to enable dispatch.';
    return 'All sections complete — ready to dispatch.';
  })();

  const btnDisabled = dispatched || !enabled;
  const tooltip = (() => {
    if (dispatched) return undefined;
    if (!enabled) return disabledTooltip;
    return undefined;
  })();

  return (
    <StyledDiv data-testid="dispatch-bar">
      <Button
        variant="primary-md"
        dataTestId="dispatch-bar-btn"
        disabled={btnDisabled}
        title={tooltip}
        onClick={handleDispatch}
      >
        {dispatched ? 'Dispatched ✓' : 'Dispatch Routes for Today'}
      </Button>
      <div className="db-status" data-testid="dispatch-bar-status">
        {status}
      </div>
    </StyledDiv>
  );
}

DispatchBar.propTypes = {
  stockingDate: PropTypes.string.isRequired,
  enabled: PropTypes.bool,
  disabledTooltip: PropTypes.string,
};

DispatchBar.defaultProps = {
  enabled: false,
  disabledTooltip:
    'Complete Line Haul Arrival and Headcount before dispatching.',
};

export default DispatchBar;
